import React, {createContext, useContext, useEffect, useState} from "react";
import {User, UserManager, WebStorageStateStore} from "oidc-client-ts";
import {useTranslation} from "react-i18next";
import {jwtDecode} from "jwt-decode";
import {IAccessTokenDecoded} from "../../app/interfaces/IUtilities";

export const userManager = new UserManager({
    authority: process.env.REACT_APP_OIDC_AUTHORITY!,
    client_id: process.env.REACT_APP_OIDC_CLIENT_ID!,
    redirect_uri: process.env.REACT_APP_OIDC_REDIRECT_URI!,
    post_logout_redirect_uri: process.env.REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI!,
    response_type: process.env.REACT_APP_OIDC_RESPONSE_TYPE!,
    scope: process.env.REACT_APP_OIDC_SCOPE!,
    userStore: new WebStorageStateStore({ store: window.localStorage })
});

type AuthContextType = {
  user: User | null;
  signinRedirect: () => void;
  signoutRedirect: () => void;
};

const authService = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    const init = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const state = urlParams.get('state');
      const savedState = localStorage.getItem('oidc_logout_state');

      if (state && savedState && state === savedState) {
        localStorage.removeItem('oidc_logout_state');
        await userManager.removeUser();
        window.history.replaceState({}, '', window.location.pathname);
        setUser(null);
        setLoading(false);
        return;
      }

      const fetchedUser = await userManager.getUser();
      setUser(fetchedUser);
      setLoading(false);
    };

    init();

    userManager.events.addUserLoaded(user => {
      setUser(user);
    });

    userManager.events.addUserUnloaded(() => {
      setUser(null);
    });

  }, []);

  const signinRedirect = () => userManager.signinRedirect();

  const signoutRedirect = async () => {
    const metadata = await userManager.metadataService.getMetadata();
    const endSessionEndpoint = metadata.end_session_endpoint;
    if (endSessionEndpoint) {
      const state = 'logout_' + Date.now();
      localStorage.setItem('oidc_logout_state', state);
      const params = new URLSearchParams();
      params.set('client_id', process.env.REACT_APP_OIDC_CLIENT_ID!);
      params.set('post_logout_redirect_uri', process.env.REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI!);
      params.set('state', state);
      window.location.href = `${endSessionEndpoint}?${params.toString()}`;
    } else {
      await userManager.signoutRedirect();
    }
  };

  return (
    <authService.Provider value={{ user, signinRedirect, signoutRedirect }}>
      {loading ? <div>{t('Loading')}</div> : children}
    </authService.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(authService);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const getAccessToken = async (): Promise<string | null> => {
  const user = await userManager.getUser();
  return user?.access_token || null;
};

export const userHasRole = async (roleKey: string): Promise<boolean> => {
  const user = await userManager.getUser();
  const access_token = user?.access_token || null;

  if (access_token) {
      const decoded = jwtDecode<IAccessTokenDecoded>(access_token);
      const roleFinded = decoded.roles.find(role => role === roleKey)
      return roleFinded !== undefined && roleFinded.length > 0;
  }
  return false;
};

